/**
 * Theme colors for different routes
 * Following modern UX and accessibility best practices:
 * - Using CSS classes defined in globals.css with hex colors
 * - Using subtle background colors with low opacity for better contrast
 * - Subtle accents for interactive elements
 * 
 * Note: Classes are defined in globals.css using hex colors for better control
 */

export type ThemeRoute = "restrail" | "safer-lc" | "newtech"

/**
 * Get theme background color class for a given route
 * Returns CSS class defined in globals.css
 * Uses low opacity (5%) for subtle, accessible backgrounds
 */
export function getThemeBackgroundColor(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "theme-restrail-bg"
    case "safer-lc":
      return "theme-safer-lc-bg"
    case "newtech":
      return "theme-newtech-bg"
    default:
      return ""
  }
}

/**
 * Get theme background color for header (slightly more visible)
 * Returns CSS class defined in globals.css
 */
export function getThemeHeaderBackground(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "theme-restrail-header-bg"
    case "safer-lc":
      return "theme-safer-lc-header-bg"
    case "newtech":
      return "theme-newtech-header-bg"
    default:
      return ""
  }
}

/**
 * Get theme background color for badges/tags (more visible but still subtle)
 * Returns CSS class defined in globals.css
 */
export function getThemeBadgeBackground(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "theme-restrail-badge-bg"
    case "safer-lc":
      return "theme-safer-lc-badge-bg"
    case "newtech":
      return "theme-newtech-badge-bg"
    default:
      return "bg-muted"
  }
}

/**
 * Get theme color class for focus ring (for accessibility)
 * Returns CSS class defined in globals.css with ring color variable
 * Use with Tailwind ring utilities: ring-2 focus-within:ring-2 or focus:ring-2
 */
export function getThemeRingColor(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "theme-restrail-ring"
    case "safer-lc":
      return "theme-safer-lc-ring"
    case "newtech":
      return "theme-newtech-ring"
    default:
      return ""
  }
}

/**
 * Get theme text color class for icons and accents
 * Returns CSS class defined in globals.css
 */
export function getThemeTextColor(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "theme-restrail-text"
    case "safer-lc":
      return "theme-safer-lc-text"
    case "newtech":
      return "theme-newtech-text"
    default:
      return "text-primary"
  }
}

/**
 * Get theme border color class for badges and borders
 * Returns CSS class that uses the theme text color for borders
 */
export function getThemeBorderColor(route: ThemeRoute): string {
  switch (route) {
    case "restrail":
      return "border-red-600"
    case "safer-lc":
      return "border-orange-600"
    case "newtech":
      return "border-cyan-600"
    default:
      return "border-border"
  }
}

/**
 * Extract route from pathname
 */
export function getRouteFromPathname(pathname: string): ThemeRoute | null {
  if (pathname.startsWith("/restrail")) return "restrail"
  if (pathname.startsWith("/safer-lc")) return "safer-lc"
  if (pathname.startsWith("/newtech")) return "newtech"
  return null
}

/**
 * Get theme route from entity type (for node pages)
 * Maps Drupal entity types to theme routes
 */
export function getThemeRouteFromEntityType(entityType: string): ThemeRoute | null {
  switch (entityType) {
    case "restrail_mesure":
    case "node_restrail_mesure":
      return "restrail"
    case "safer_lc_measure":
    case "node_safer_lc_measure":
      return "safer-lc"
    case "new_technologie":
    case "node_new_technologie":
      return "newtech"
    default:
      return null
  }
}
